import { useState } from 'react'

import { Button, Spacer } from '@chakra-ui/react'
import { useQuery } from '@rocicorp/zero/react'
import { Box, EmptyState, GridList, HStack, Page, Text } from '@saas-ui/react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { RelativeTime } from '@trevize/i18n'
import { SegmentedControl } from '@trevize/ui/segmented-control'

import { DefaultLoader } from '#components/default-loader.tsx'
import { FileIcon } from '#components/icons/file-icon.tsx'
import { PlusIcon } from '#components/icons/plus-icon.tsx'
import { PageHeader } from '#components/page-header.tsx'
import { BriefStatusBadge } from '#features/briefs/components/brief-status.tsx'
import { useCreateDialogActions } from '#features/common/components/create-dialog-store.ts'
import { useCurrentWorkspace } from '#features/common/hooks/use-current-workspace.ts'
import { queries } from '#zero/queries.ts'

const PAGE_SIZE = 20

const searchSchema = z.object({
  filter: z.enum(['active', 'archived']).catch('active').default('active'),
})

const filterSegments = [
  { id: 'active', label: 'Active' },
  { id: 'archived', label: 'Archived' },
]

export const Route = createFileRoute('/_app/$workspace/_dashboard/briefs/')({
  component: BriefsListPage,
  pendingComponent: DefaultLoader,
  validateSearch: searchSchema,
})

function BriefsListPage() {
  const [workspace] = useCurrentWorkspace()
  const { workspace: workspaceSlug } = Route.useParams()
  const { filter } = Route.useSearch()
  const navigate = Route.useNavigate()

  const actions = useCreateDialogActions()

  const [limit, setLimit] = useState(PAGE_SIZE)

  const [briefs, queryResult] = useQuery(
    queries.briefs.list({
      workspaceId: workspace.id,
      filter,
      limit: limit + 1,
    }),
  )
  const isLoading = queryResult.type === 'unknown'
  const hasMore = briefs.length > limit
  const items = hasMore ? briefs.slice(0, limit) : briefs

  const handleFilterChange = (value: string) => {
    setLimit(PAGE_SIZE)
    navigate({ search: { filter: value as 'active' | 'archived' } })
  }

  return (
    <Page.Root>
      <PageHeader
        title="Briefs"
        actions={
          <HStack>
            <SegmentedControl
              segments={filterSegments}
              value={filter}
              onChange={handleFilterChange}
              size="xs"
            />
            <Spacer />
            <Button size="xs" onClick={() => actions.open()}>
              <PlusIcon />
              New
            </Button>
          </HStack>
        }
      />
      <Page.Body p="0">
        {isLoading && <DefaultLoader />}
        {!isLoading && items.length === 0 && (
          <EmptyState
            title={
              filter === 'archived' ? 'No archived briefs' : 'No briefs yet'
            }
            description={
              filter === 'archived'
                ? 'Archived briefs will appear here'
                : 'Create your first brief to get started'
            }
          />
        )}
        {items.length > 0 && (
          <>
            <GridList.Root py="0">
              {items.map((brief) => (
                <GridList.Item
                  key={brief.id}
                  asChild
                  py="2"
                  borderBottomWidth="1px"
                  cursor="pointer"
                  _hover={{ bg: 'bg.subtle' }}
                  css={{ '&:last-of-type': { borderWidth: 0 } }}
                  gap="0"
                >
                  <Link
                    to="/$workspace/briefs/$id"
                    params={{ workspace: workspaceSlug, id: brief.id }}
                  >
                    <GridList.Cell>
                      <FileIcon size="18" />
                    </GridList.Cell>
                    <GridList.Cell
                      flex="1"
                      px="4"
                      flexDirection="column"
                      gap="0"
                    >
                      <Text textStyle="sm" fontWeight="medium">
                        {brief.title}
                      </Text>
                    </GridList.Cell>
                    <GridList.Cell
                      display="flex"
                      flexDirection="row"
                      justifyContent="flex-end"
                      alignItems="center"
                      gap="2"
                    >
                      <BriefStatusBadge
                        status={brief.status ?? 'draft'}
                        py="1"
                        px="2"
                      />
                      <Box
                        color="fg.muted"
                        textStyle="xs"
                        width="100px"
                        textAlign="right"
                      >
                        <RelativeTime
                          date={
                            new Date(brief.updatedAt ?? brief.createdAt ?? 0)
                          }
                        />
                      </Box>
                    </GridList.Cell>
                  </Link>
                </GridList.Item>
              ))}
            </GridList.Root>

            {hasMore && (
              <HStack justify="center" mt="6">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLimit((n) => n + PAGE_SIZE)}
                >
                  Load more
                </Button>
              </HStack>
            )}
          </>
        )}
      </Page.Body>
    </Page.Root>
  )
}
